import FavoriteIcon from "@mui/icons-material/Favorite";
import { useMediaQuery, useTheme, alpha } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Unstable_Grid2";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTranslation } from "react-i18next";
import { Seo } from "src/components/seo";
import { usePageView } from "src/hooks/use-page-view";

const Page = () => {
  const { t } = useTranslation();
  usePageView();
  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));

  const donationLink = "https://donate.stripe.com/eVq14ncov5L30uL75feZ202";

  return (
    <>
      <Seo title="Donate" />
      <Box component="main" sx={{ flexGrow: 1 }}>
        {/* Hero Section */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            pt: smDown ? 8 : 12,
            pb: smDown ? 2 : 3,
            position: "relative",
            overflow: "hidden",
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                textAlign: "center",
                maxWidth: "800px",
                mx: "auto",
                mt: 2,
              }}
            >
              <Typography
                variant={smDown ? "h3" : "h2"}
                sx={{
                  color: theme.palette.primary.main,
                  fontWeight: "bold",
                  fontSize: smDown ? "2rem" : "3rem",
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                Support Our Mission
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  maxWidth: "600px",
                  mx: "auto",
                  lineHeight: 1.7,
                }}
              >
                Your donation helps us continue providing open-source healthcare
                solutions.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Donation Card Section */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            pb: smDown ? 6 : 10,
          }}
        >
          <Container maxWidth="md">
            <Card
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
                border: "1px solid #F3F4F6",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <CardContent
                sx={{
                  p: { xs: 4, md: 6 },
                  textAlign: "center",
                }}
              >
                <Stack spacing={4} alignItems="center">
                  <Box
                    sx={{
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      borderRadius: "50%",
                      p: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <FavoriteIcon
                      sx={{
                        fontSize: 60,
                        color: theme.palette.primary.main,
                      }}
                    />
                  </Box>

                  <Box>
                    <Typography
                      variant={smDown ? "h5" : "h4"}
                      sx={{
                        fontWeight: "bold",
                        mb: 2,
                        color: theme.palette.text.primary,
                      }}
                    >
                      Make a Donation
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.8,
                        fontSize: { xs: "0.95rem", md: "1rem" },
                        maxWidth: "500px",
                        mb: 1,
                      }}
                    >
                      Every contribution matters
                    </Typography>
                  </Box>

                  <Box sx={{ mt: 2, width: "100%", maxWidth: "400px" }}>
                    <Button
                      component="a"
                      href={donationLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<FavoriteIcon />}
                      sx={{
                        py: 2,
                        px: 4,
                        fontSize: "1.1rem",
                        fontWeight: 600,
                        borderRadius: 2,
                        textTransform: "none",
                        background: `linear-gradient(135deg, ${
                          theme.palette.primary.main
                        } 0%, ${alpha(theme.palette.primary.main, 0.8)} 100%)`,
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                        "&:hover": {
                          background: `linear-gradient(135deg, ${alpha(
                            theme.palette.primary.main,
                            0.9
                          )} 0%, ${alpha(
                            theme.palette.primary.main,
                            0.7
                          )} 100%)`,
                          boxShadow: "0 6px 25px rgba(0, 0, 0, 0.2)",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Donate Now
                    </Button>
                  </Box>

                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      mt: 2,
                      fontSize: "0.75rem",
                      opacity: 0.7,
                    }}
                  >
                    Secure donation powered by Stripe
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          </Container>
        </Box>

        {/* Benefits Section */}
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
            py: smDown ? 6 : 10,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant={smDown ? "h4" : "h3"}
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                mb: 6,
                color: theme.palette.text.primary,
              }}
            >
              How Your Donation Helps
            </Typography>
            <Grid container spacing={4}>
              {[
                {
                  title: "Open-source software development",
                  description:
                    "Members of our team actively participate in the open-source community.",
                },
                {
                  title: "Community Support",
                  description: (
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          "Help us provide free resources, more <a href='https://www.opensource.clinic/blog' target='_blank'>tutorials</a>, and support to the community.",
                      }}
                    />
                  ),
                },
                {
                  title: "Research & Innovation",
                  description:
                    "Contribute to research and development of new open-source diabetes technologies and treatment methods.",
                },
              ].map((benefit, index) => (
                <Grid xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      backgroundColor: "#FFFFFF",
                      borderRadius: 3,
                      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                      border: "1px solid #F3F4F6",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 30px rgba(0, 0, 0, 0.12)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: "bold",
                          mb: 2,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {benefit.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          lineHeight: 1.7,
                        }}
                      >
                        {benefit.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Thank You Section */}
        <Box
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.5),
            py: smDown ? 6 : 10,
            color: "white",
            mt: 6,
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant={smDown ? "h4" : "h3"}
                sx={{
                  fontWeight: "bold",
                  mb: 3,
                  color: "white",
                }}
              >
                Thank You for Your Support
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  mb: 4,
                  maxWidth: "600px",
                  mx: "auto",
                  opacity: 0.9,
                  lineHeight: 1.7,
                }}
              >
                Your generosity enables us to continue our mission of making
                healthcare technology accessible to everyone. Every donation
                helps us move closer to our goal of improving medical progress
                for all.
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default Page;
